import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import {
  Search, MoreHorizontal, KeyRound, Mail, Lock, Power, UserCheck, X,
  ShieldAlert, RefreshCw, Eye, EyeOff, Copy, CheckCircle2, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { setPreviewSession } from '../../lib/previewSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: 'all', label: 'All', filter: { role: 'all' } },
  { key: 'employees', label: 'Employees', filter: { role: 'employee,technician,manager,service_manager,construction_manager,office_staff' }, roles: ['employee', 'technician', 'manager', 'service_manager', 'construction_manager', 'office_staff'] },
  { key: 'customers', label: 'Customers', filter: { role: 'customer' } },
  { key: 'vips', label: 'VIPs', filter: { role: 'vip' } },
  { key: 'pm', label: 'Property Mgrs', filter: { role: 'property_manager,asset_manager,maintenance_supervisor' }, roles: ['property_manager', 'asset_manager', 'maintenance_supervisor'] },
  { key: 'admins', label: 'Admins', filter: { role: 'admin,super_admin' }, roles: ['admin', 'super_admin'] },
  { key: 'pending', label: 'Pending Approvals', filter: { status: 'pending_approval', role: 'all' } },
];

const STATUS_STYLE = {
  active: 'bg-green-900/30 text-green-300 border-green-700/50',
  inactive: 'bg-red-900/30 text-red-300 border-red-700/50',
  pending_approval: 'bg-amber-900/30 text-amber-300 border-amber-700/50',
  pending_email: 'bg-blue-900/30 text-blue-300 border-blue-700/50',
};

const STATUS_LABEL = {
  active: 'Active',
  inactive: 'Inactive',
  pending_approval: 'Pending Approval',
  pending_email: 'Email Unverified',
};

const ROLE_LABEL = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  service_manager: 'Service Mgr',
  construction_manager: 'Construction Mgr',
  office_staff: 'Office Staff',
  technician: 'Technician',
  employee: 'Employee',
  customer: 'Customer',
  vip: 'VIP',
  property_manager: 'Property Manager',
  asset_manager: 'Asset Manager',
  maintenance_supervisor: 'Maintenance Supervisor',
};

const fmtDate = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '—';
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return `Today ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
};

const UserManagement = () => {
  const { user: me, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ rows: [], total: 0, role_counts: {}, pending_approval_count: 0, page: 1, page_size: 50 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');

  // Dialogs
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [resetForce, setResetForce] = useState(true);
  const [resetBusy, setResetBusy] = useState(false);
  const [linkDialog, setLinkDialog] = useState(null);

  const tabDef = TABS.find((t) => t.key === tab) || TABS[0];

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (tabDef.filter.role) params.set('role', tabDef.filter.role);
      if (tabDef.filter.status) params.set('status', tabDef.filter.status);
      if (search.trim()) params.set('search', search.trim());
      params.set('page', String(page));
      params.set('page_size', '50');
      const { data: resp } = await axios.get(`${API}/admin/users?${params.toString()}`, { withCredentials: true });
      // Optionally widen "employees" tab to include all internal-staff roles
      if (tab === 'employees') {
        resp.rows = resp.rows.filter((r) => (tabDef.roles || []).includes(r.role));
      }
      if (tab === 'admins') {
        resp.rows = resp.rows.filter((r) => (tabDef.roles || []).includes(r.role));
      }
      if (tab === 'pm') {
        resp.rows = resp.rows.filter((r) => (tabDef.roles || []).includes(r.role));
      }
      setData(resp);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load users');
    }
    setLoading(false);
  }, [tab, tabDef, search, page]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Reset to page 1 when tab or search changes
  useEffect(() => { setPage(1); }, [tab, search]);

  const showLink = (link) => setLinkDialog(link);

  const action = async (u, type) => {
    try {
      if (type === 'send-reset') {
        const { data: r } = await axios.post(`${API}/admin/users/${u.id}/send-password-reset`, {}, { withCredentials: true });
        if (r.delivery_mode === 'preview') {
          showLink(r.reset_link);
        } else {
          toast.success(`Reset link emailed to ${u.email}`);
        }
        fetchRows();
        return;
      }
      if (type === 'force-pw') {
        await axios.post(`${API}/admin/users/${u.id}/force-password-change`, {}, { withCredentials: true });
        toast.success(`${u.name} will be prompted to change password on next login`);
        fetchRows();
        return;
      }
      if (type === 'deactivate') {
        if (!window.confirm(`Deactivate ${u.name}? They will not be able to sign in until reactivated.`)) return;
        await axios.put(`${API}/admin/users/${u.id}/deactivate`, {}, { withCredentials: true });
        toast.success(`${u.name} deactivated`);
        fetchRows();
        return;
      }
      if (type === 'activate') {
        await axios.put(`${API}/admin/users/${u.id}/activate`, {}, { withCredentials: true });
        toast.success(`${u.name} activated`);
        fetchRows();
        return;
      }
      if (type === 'approve') {
        await axios.post(`${API}/admin/users/${u.id}/approve`, {}, { withCredentials: true });
        toast.success(`${u.name} approved`);
        fetchRows();
        return;
      }
      if (type === 'reject') {
        if (!window.confirm(`Reject ${u.name}? Their signup will be cancelled.`)) return;
        await axios.post(`${API}/admin/users/${u.id}/reject`, {}, { withCredentials: true });
        toast.success(`${u.name} rejected`);
        fetchRows();
        return;
      }
      if (type === 'impersonate') {
        if (!window.confirm(`View the app as ${u.name}? Every action you take will be audit-logged as them.`)) return;
        await axios.post(`${API}/admin/users/${u.id}/impersonate`, {}, { withCredentials: true });
        toast.success(`Now acting as ${u.name}`);
        // Route to the appropriate landing page for that role
        const pmRoles = ['property_manager', 'asset_manager', 'maintenance_supervisor'];
        const land = u.role === 'customer' ? '/customer/dashboard'
                   : u.role === 'vip' ? '/vip/dashboard'
                   : u.role === 'technician' || u.role === 'employee' ? '/employee/timeclock'
                   : pmRoles.includes(u.role) ? '/pm/dashboard'
                   : '/admin/dashboard';
        // Full page reload so AuthContext + every route guard re-reads the new session cookie cleanly.
        window.location.href = land;
        return;
      }
      if (type === 'preview') {
        if (!window.confirm(`Preview the app as ${u.name}? READ-ONLY — all writes are blocked.`)) return;
        const { data } = await axios.post(`${API}/admin/preview/start`, { user_id: u.id }, { withCredentials: true });
        setPreviewSession({
          user_id: u.id,
          name: u.name,
          email: u.email,
          role: u.role,
          started_at: data?.preview?.started_at,
        });
        toast.success(`Previewing as ${u.name} (read-only)`);
        const pmRoles = ['property_manager', 'asset_manager', 'maintenance_supervisor'];
        const land = u.role === 'customer' ? '/customer/dashboard'
                   : u.role === 'vip' ? '/vip/dashboard'
                   : u.role === 'technician' || u.role === 'employee' ? '/employee/timeclock'
                   : pmRoles.includes(u.role) ? '/pm/dashboard'
                   : '/admin/dashboard';
        // Full reload so the preview cookies are picked up by the global app shell.
        window.location.href = land;
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const submitReset = async () => {
    if (!resetPw || resetPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setResetBusy(true);
    try {
      await axios.post(`${API}/admin/users/${resetTarget.id}/password-reset`,
        { new_password: resetPw, force_change_on_next_login: resetForce },
        { withCredentials: true });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null);
      setResetPw('');
      setResetForce(true);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
    setResetBusy(false);
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 50)));

  return (
    <div data-testid="user-mgmt-page" className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em]">ADMIN</p>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-white">User Management</h1>
          <p className="text-[#A3A3A3] text-sm">All accounts in one place — employees, customers, VIPs, and admins.</p>
        </div>
        <Button data-testid="users-refresh" onClick={fetchRows} variant="outline" className="border-[#262626] text-[#A3A3A3] hover:text-white">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => {
          const count = t.key === 'pending'
            ? data.pending_approval_count
            : t.key === 'all'
              ? data.total
              : null;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`users-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                active ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]' : 'bg-[#141414] text-[#A3A3A3] border-[#262626] hover:text-white hover:border-[#D4AF37]/50'
              }`}
            >
              {t.label}
              {count != null && count > 0 && (
                <span className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded ${active ? 'bg-[#0A0A0A]/20 text-[#0A0A0A]' : t.key === 'pending' ? 'bg-amber-500/20 text-amber-300' : 'bg-[#262626] text-white'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="bg-[#0D0D0D] border border-[#1f1f1f] rounded-lg p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#525252]" />
          <Input
            data-testid="users-search"
            placeholder="Search by name, email, or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-[#141414] border-[#262626] text-white h-11"
          />
        </div>
      </div>

      {error && (
        <div data-testid="users-error" className="bg-[#7F1D1D]/30 border border-[#7F1D1D] text-red-200 rounded-md p-3 text-sm">{error}</div>
      )}

      {/* Table */}
      <div className="bg-[#141414] border border-[#262626] rounded-lg overflow-x-auto">
        <table className="w-full min-w-[800px] text-sm">
          <thead className="bg-[#0A0A0A] border-b border-[#262626]">
            <tr>
              <th className="text-left px-4 py-3 text-[#A3A3A3] text-xs uppercase tracking-wider">Name</th>
              <th className="text-left px-4 py-3 text-[#A3A3A3] text-xs uppercase tracking-wider">Role</th>
              <th className="text-left px-4 py-3 text-[#A3A3A3] text-xs uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-3 text-[#A3A3A3] text-xs uppercase tracking-wider hidden md:table-cell">Department</th>
              <th className="text-left px-4 py-3 text-[#A3A3A3] text-xs uppercase tracking-wider hidden lg:table-cell">Last Login</th>
              <th className="text-right px-4 py-3 text-[#A3A3A3] text-xs uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && data.rows.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-[#525252]">Loading…</td></tr>
            ) : data.rows.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-[#525252]">No users match this filter.</td></tr>
            ) : data.rows.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#262626] last:border-0 hover:bg-[#0A0A0A]/40">
                <td className="px-4 py-3 text-white">
                  <div className="flex flex-col">
                    <span className="font-medium">{u.name || '—'}</span>
                    <span className="text-[#525252] text-xs">{u.email}</span>
                    {u.phone && <span className="text-[#525252] text-xs">{u.phone}</span>}
                    {u.must_change_password && (
                      <span className="inline-flex items-center gap-1 mt-0.5 text-amber-400 text-[10px]">
                        <Clock className="w-3 h-3" /> Must change password on next login
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-[#D4AF37] text-xs font-medium uppercase tracking-wider">{ROLE_LABEL[u.role] || u.role}</span>
                </td>
                <td className="px-4 py-3">
                  <Badge className={`${STATUS_STYLE[u.status] || STATUS_STYLE.active} text-[10px] uppercase tracking-wider`}>
                    {STATUS_LABEL[u.status] || u.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-[#A3A3A3] hidden md:table-cell uppercase text-xs tracking-wider">{u.department || '—'}</td>
                <td className="px-4 py-3 text-[#A3A3A3] hidden lg:table-cell text-xs">{fmtDate(u.last_login_at)}</td>
                <td className="px-4 py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button data-testid={`user-actions-${u.id}`} size="sm" variant="ghost" className="text-[#A3A3A3] hover:text-white">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626] text-white min-w-[220px]">
                      <DropdownMenuLabel className="text-[#525252] text-xs">{u.name}</DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-[#262626]" />
                      {u.status === 'pending_approval' ? (
                        <>
                          <DropdownMenuItem data-testid={`user-approve-${u.id}`} onClick={() => action(u, 'approve')} className="text-green-400 focus:bg-green-900/30 focus:text-green-300">
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Approve signup
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`user-reject-${u.id}`} onClick={() => action(u, 'reject')} className="text-red-400 focus:bg-red-900/30 focus:text-red-300">
                            <X className="w-4 h-4 mr-2" /> Reject signup
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-[#262626]" />
                        </>
                      ) : null}
                      <DropdownMenuItem data-testid={`user-reset-pw-${u.id}`} onClick={() => { setResetTarget(u); setResetPw(''); setResetForce(true); }} className="focus:bg-[#262626]">
                        <KeyRound className="w-4 h-4 mr-2" /> Reset password
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`user-send-reset-${u.id}`} onClick={() => action(u, 'send-reset')} className="focus:bg-[#262626]">
                        <Mail className="w-4 h-4 mr-2" /> Send reset email
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`user-force-pw-${u.id}`} onClick={() => action(u, 'force-pw')} className="focus:bg-[#262626]">
                        <Lock className="w-4 h-4 mr-2" /> Force password change
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#262626]" />
                      {u.active === false ? (
                        <DropdownMenuItem data-testid={`user-activate-${u.id}`} onClick={() => action(u, 'activate')} className="text-green-400 focus:bg-green-900/30 focus:text-green-300">
                          <UserCheck className="w-4 h-4 mr-2" /> Reactivate
                        </DropdownMenuItem>
                      ) : (
                        u.id !== me?._id && (
                          <DropdownMenuItem data-testid={`user-deactivate-${u.id}`} onClick={() => action(u, 'deactivate')} className="text-red-400 focus:bg-red-900/30 focus:text-red-300">
                            <Power className="w-4 h-4 mr-2" /> Deactivate
                          </DropdownMenuItem>
                        )
                      )}
                      {u.active !== false && u.id !== me?._id && (
                        <>
                          <DropdownMenuSeparator className="bg-[#262626]" />
                          <DropdownMenuItem data-testid={`user-preview-${u.id}`} onClick={() => action(u, 'preview')} className="text-cyan-400 focus:bg-cyan-900/30 focus:text-cyan-300">
                            <Eye className="w-4 h-4 mr-2" /> View Their Side (read-only)
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`user-impersonate-${u.id}`} onClick={() => action(u, 'impersonate')} className="text-amber-400 focus:bg-amber-900/30 focus:text-amber-300">
                            <EyeOff className="w-4 h-4 mr-2" /> Impersonate (full session)
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-[#A3A3A3]">
          <span>Showing {data.rows.length} of {data.total} · Page {data.page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button data-testid="users-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} variant="outline" size="sm" className="border-[#262626] text-[#A3A3A3]">Prev</Button>
            <Button data-testid="users-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} variant="outline" size="sm" className="border-[#262626] text-[#A3A3A3]">Next</Button>
          </div>
        </div>
      )}

      {/* Password reset dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent data-testid="reset-password-dialog" className="bg-[#141414] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#D4AF37]" /> Reset Password</DialogTitle>
            <DialogDescription className="text-[#A3A3A3] text-xs">
              Set a new password for <span className="text-white font-medium">{resetTarget?.name}</span>. They will be required to change it on next login by default.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white text-xs">New password</Label>
              <Input
                data-testid="reset-pw-input"
                type="text"
                value={resetPw}
                onChange={(e) => setResetPw(e.target.value)}
                placeholder="Temp pass — at least 8 chars"
                className="bg-[#0A0A0A] border-[#262626] text-white font-mono h-11"
                autoFocus
              />
              <Button
                data-testid="reset-pw-suggest"
                onClick={() => setResetPw(`Nx-${Math.random().toString(36).slice(2, 6).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`)}
                variant="ghost"
                size="sm"
                className="mt-1 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs"
              >
                Suggest a strong temp password
              </Button>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
              <input
                data-testid="reset-pw-force"
                type="checkbox"
                checked={resetForce}
                onChange={(e) => setResetForce(e.target.checked)}
                className="rounded border-[#262626] bg-[#0A0A0A]"
              />
              Force this user to change password on next login (recommended)
            </label>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button onClick={() => setResetTarget(null)} variant="outline" className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
            <Button data-testid="reset-pw-submit" onClick={submitReset} disabled={resetBusy} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
              {resetBusy ? 'Resetting…' : 'Reset password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset-link "delivered" dialog (mocked email mode) */}
      <Dialog open={!!linkDialog} onOpenChange={(o) => !o && setLinkDialog(null)}>
        <DialogContent data-testid="reset-link-dialog" className="bg-[#141414] border-[#262626] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#D4AF37]" /> Reset link generated</DialogTitle>
            <DialogDescription className="text-[#A3A3A3] text-xs">
              Email delivery is currently in preview mode — give this link directly to the user. It expires in 2 hours.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 break-all text-xs text-[#D4AF37] font-mono">
            {linkDialog}
          </div>
          <div className="flex gap-2 justify-between items-center">
            <p className="text-amber-300 text-[10px] flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Once Resend is configured, this will be emailed automatically.</p>
            <Button
              data-testid="copy-reset-link"
              onClick={() => { navigator.clipboard.writeText(linkDialog); toast.success('Copied'); }}
              size="sm"
              className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
